// ---------------------------------------------------------------------------
// HART Trend Detection Frontend — Main Entry Point
// ---------------------------------------------------------------------------

import { CONFIG, STATE, LIFERAFT_LOOKBACK_DAYS, LIFERAFT_COMPARE_DAYS } from './config';
import { ML_CAMPAIGN_TYPES, SENTIMENT_STYLES } from './constants';
import { api } from './api';
import {
  showLoading, hideLoading, showToast, openModal, closeTrendModal,
  updateConnectionStatus, clearDashboardData,
  showTrendsPlaceholder, showTopicsPlaceholder, showAnalyticsPlaceholder,
  showDashboardErrorPlaceholder,
} from './ui';
import {
  classifyHartEventByCampaignType, analyzeSentimentFromDescription,
  getSentimentValue, determineRiskLevel,
  calculateTrendChange, getTrendIndicatorHTML,
  extractKeywordsFromText, filterKeywords,
  extractDateFromDescription, extractLocationFromDescription,
  formatNumber, formatDateRelative, getSourceIcon,
} from './utils';
import {
  createHartCharts, createHartAnalyticsCharts,
  createSpikesChartFromTopics, createSpikesChartFromDailyVolumes, createSentimentChartFromTopics,
  createCampaignTypeChartFromTopics, createTopicAnalyticsCharts,
} from './charts';
import type {
  HartEvent, HartEventsResponse, Post, TopicCluster, Trend, TrendChange, DailyVolume,
} from './types';

// ═══════════════════════════════════════════════════════════════════════════
// Initialization
// ═══════════════════════════════════════════════════════════════════════════

document.addEventListener('DOMContentLoaded', () => initializeApp());

// ═══════════════════════════════════════════════════════════════════════════
// Authorization guard
// ═══════════════════════════════════════════════════════════════════════════

const AUTH_REDIRECT_URL = 'https://hart.horussecuretravel.com';

function showUnauthorizedOverlay(): void {
  const overlay = document.getElementById('unauthorizedOverlay');
  if (overlay) overlay.classList.add('active');

  let countdown = 3;
  const countdownEl = document.getElementById('redirectCountdown');

  const timer = setInterval(() => {
    countdown--;
    if (countdownEl) countdownEl.textContent = String(countdown);
    if (countdown <= 0) {
      clearInterval(timer);
      window.location.href = `${AUTH_REDIRECT_URL}/#/?redirectUrl=${encodeURIComponent(window.location.href)}`;
    }
  }, 1000);
}

async function checkAuthorization(): Promise<boolean> {
  try {
    const response = await fetch(`${CONFIG.apiBaseUrl}/health`, { method: 'GET' });
    if (response.status === 401) {
      showUnauthorizedOverlay();
      return false;
    }
    return true;
  } catch (error) {
    // Network error — allow app to proceed; API calls will surface the issue
    console.error('Authorization check failed:', error);
    return true;
  }
}

async function initializeApp(): Promise<void> {
  console.log('Initializing HART Trend Detection Frontend...');
  console.log('API base URL:', CONFIG.apiBaseUrl);

  // Security check — must be authorized before loading anything
  const isAuthorized = await checkAuthorization();
  if (!isAuthorized) return;

  setInitialDates();
  loadSettings();
  applyTheme(CONFIG.theme);
  await checkConnection();
  showSection('dashboard', true);

  if (CONFIG.currentAPI === 'hart') {
    await loadHartDashboardData();
  } else {
    await loadDashboardData();
  }

  if (STATE.autoRefreshEnabled) startAutoRefresh();
  console.log('Application initialized successfully');
}

function setInitialDates(): void {
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86_400_000).toISOString().split('T')[0];
  (document.getElementById('analyticsStartDate') as HTMLInputElement).value = yesterday;
  (document.getElementById('analyticsEndDate') as HTMLInputElement).value = today;
  STATE.currentDate = today;
}

// ═══════════════════════════════════════════════════════════════════════════
// Navigation
// ═══════════════════════════════════════════════════════════════════════════

function showSection(sectionName: string, skipLoad = false): void {
  document.querySelectorAll('.nav-item').forEach(i => i.classList.remove('active'));
  document.querySelector(`[onclick="showSection('${sectionName}')"]`)?.classList.add('active');
  document.querySelectorAll('.content-section').forEach(s => s.classList.remove('active'));
  document.getElementById(`${sectionName}-section`)?.classList.add('active');
  STATE.currentSection = sectionName;
  if (!skipLoad) loadCurrentSectionData();
}

async function loadCurrentSectionData(): Promise<void> {
  const isHart = CONFIG.currentAPI === 'hart';
  const loaders: Record<string, () => Promise<void>> = {
    dashboard: isHart ? loadHartDashboardData : loadDashboardData,
    trends:    isHart ? loadHartTrends : loadTrends,
    analytics: isHart ? loadHartAnalytics : loadAnalytics,
  };
  const fn = loaders[STATE.currentSection];
  if (fn) await fn();
}

// ═══════════════════════════════════════════════════════════════════════════
// API Switching
// ═══════════════════════════════════════════════════════════════════════════

async function switchAPI(apiSource: string): Promise<void> {
  CONFIG.currentAPI = apiSource;
  api.switchSource(apiSource);

  document.querySelectorAll('.api-tab').forEach(t => t.classList.remove('active'));
  document.querySelector(`[data-api="${apiSource}"]`)?.classList.add('active');

  const name = CONFIG.apiSources[apiSource].name;
  const titleEl = document.getElementById('navbar-title');
  if (titleEl) titleEl.textContent = `HART Trend Detection - ${name}`;
  clearDashboardData();
  await loadCurrentSectionData();
}

// ═══════════════════════════════════════════════════════════════════════════
// Connection
// ═══════════════════════════════════════════════════════════════════════════

async function checkConnection(): Promise<void> {
  try {
    if (CONFIG.currentAPI === 'hart') await api.getHartEvents(0, 1);
    else await api.healthCheck();
    updateConnectionStatus(true);
    showToast(`Connected to ${CONFIG.apiSources[CONFIG.currentAPI].name} API`, 'success');
  } catch {
    updateConnectionStatus(false);
    showToast(`Failed to connect to ${CONFIG.apiSources[CONFIG.currentAPI].name} API`, 'error');
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// HART – helpers for loading historical data (deduplicated pattern)
// ═══════════════════════════════════════════════════════════════════════════

async function fetchHartWithHistory(count: number): Promise<{ data: HartEventsResponse; historical: HartEventsResponse | null }> {
  const data = await api.getHartEvents(STATE.hartPage, count);
  let historical: HartEventsResponse | null = null;
  try {
    const prevPage = STATE.hartPage > 0 ? STATE.hartPage - 1 : 1;
    if (prevPage !== STATE.hartPage) historical = await api.getHartEvents(prevPage, count);
  } catch { /* no historical data available */ }
  return { data, historical };
}

/** Fetch ~1 week of events: 7 pages for current week, 7 for previous week (for weekly % change). */
async function fetchHartWeekly(): Promise<{ current: HartEvent[]; previous: HartEvent[] }> {
  const PAGE_SIZE = 50;
  const PAGES_PER_WEEK = 7;
  const current: HartEvent[] = [];
  const previous: HartEvent[] = [];
  for (let p = 0; p < PAGES_PER_WEEK; p++) {
    const r = await api.getHartEvents(p, PAGE_SIZE);
    if (r.events?.length) current.push(...r.events);
  }
  for (let p = PAGES_PER_WEEK; p < PAGES_PER_WEEK * 2; p++) {
    const r = await api.getHartEvents(p, PAGE_SIZE);
    if (r.events?.length) previous.push(...r.events);
  }
  return { current, previous };
}

function groupByCampaign(events: HartEvent[]): Record<string, HartEvent[]> {
  const groups: Record<string, HartEvent[]> = {};
  events.forEach(e => {
    const t = e.mlCampaignType && e.mlCampaignType !== 'Unknown' ? e.mlCampaignType : classifyHartEventByCampaignType(e);
    (groups[t] ??= []).push(e);
  });
  return groups;
}

// ═══════════════════════════════════════════════════════════════════════════
// HART – Dashboard
// ═══════════════════════════════════════════════════════════════════════════

async function loadHartDashboardData(): Promise<void> {
  try {
    showLoading('Loading HART events data...');
    const { data, historical } = await fetchHartWithHistory(STATE.hartPageCount);
    hideLoading();
    STATE.hartEventsData = data;
    displayHartEvents(data, historical);
    updateHartMetrics(data);
  } catch (err: unknown) {
    hideLoading();
    const e = err as Error;
    showToast('Error loading HART events: ' + e.message, 'error');
    const c = document.getElementById('dashboardTopicsGrid');
    if (c) c.innerHTML = `<div class="topics-placeholder"><i class="fas fa-exclamation-triangle" style="color:#f56565"></i><p>Failed to load HART events</p></div>`;
    createHartCharts([]); // Replace loading spinners with "no data" placeholders
  }
}

function updateHartMetrics(data: HartEventsResponse): void {
  if (!data?.events) return;
  const events = data.events;
  const types = new Set(events.map(classifyHartEventByCampaignType));
  const el = (id: string) => document.getElementById(id);

  el('totalTrends')!.textContent = String(types.size);
  el('totalTopics')!.textContent = String(types.size);
  el('totalVolume')!.textContent = formatNumber(data.totalCount || events.length);

  let total = 0, count = 0;
  events.forEach(e => { total += getSentimentValue(analyzeSentimentFromDescription(e.descBody)); count++; });
  const avg = count > 0 ? total / count : 0;
  el('avgSentiment')!.textContent = (((avg + 1) / 2) * 10).toFixed(1);

  ['trendsChange','topicsChange','volumeChange','sentimentChange'].forEach(id => { const x = el(id); if (x) x.style.display = 'none'; });
}

function displayHartEvents(data: HartEventsResponse, hist: HartEventsResponse | null): void {
  if (!data?.events) return;
  const events = data.events;
  const histEvents = hist?.events || [];
  const curGroups = groupByCampaign(events);
  const histGroups = groupByCampaign(histEvents);
  const trends: Record<string, TrendChange | null> = {};
  for (const t of Object.keys(curGroups)) trends[t] = calculateTrendChange(curGroups[t].length, histGroups[t]?.length || 0);

  const container = document.getElementById('dashboardTopicsGrid');
  if (!container) return;
  if (events.length === 0) {
    container.innerHTML = '<div class="topics-placeholder"><i class="fas fa-shield-alt"></i><p>No HART events available</p></div>';
    createHartCharts([]); // Replace loading spinners with "no data" placeholders
    return;
  }

  const header = `<div style="grid-column:1/-1;background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);color:white;padding:16px;border-radius:12px;margin-bottom:16px;display:flex;align-items:center;gap:12px;box-shadow:0 4px 12px rgba(102,126,234,0.3)"><i class="fas fa-shield-alt" style="font-size:1.5rem"></i><div><div style="font-weight:600;font-size:1.125rem">HART Security Events</div><div style="font-size:0.875rem;opacity:0.9">Real-time security and travel alerts</div></div><div style="margin-left:auto;background:rgba(255,255,255,0.2);padding:8px 16px;border-radius:20px;font-weight:600">${events.length} Events</div></div>`;

  container.innerHTML = header + events.map((event, index) => {
    const desc = event.descBody || 'No description';
    const type = event.eventIncidentType || event.campaignType || 'General Alert';
    const title = desc.substring(0, 80) + (desc.length > 80 ? '...' : '');
    const camp = classifyHartEventByCampaignType(event);
    const ind = getTrendIndicatorHTML(trends[camp]);
    const dateMatch = desc.match(/(?:On|From)\s+(\d{1,2}\s+\w+\s+\d{4})/i);
    const dateLabel = dateMatch ? dateMatch[0] : 'Recent';
    const risk = determineRiskLevel(desc);
    const locMatch = desc.match(/\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:reported|accused|confirmed|said)/);
    const loc = locMatch ? locMatch[1] : 'Multiple Locations';

    return `<div class="topic-card hart-event-card" onclick='showHartEventDetails(${JSON.stringify(event).replace(/'/g,"&apos;")},${index})'>
      <div class="topic-header"><div class="topic-title" style="display:flex;align-items:center;gap:8px;justify-content:space-between;flex-wrap:wrap"><div>${type}<span class="topic-date">${dateLabel}</span></div>${ind}</div></div>
      <div class="hart-event-info"><div class="hart-event-title">${title}</div><div class="hart-event-meta"><span style="color:${risk.color};font-weight:600"><i class="fas fa-exclamation-triangle"></i> ${risk.level}</span><span><i class="fas fa-map-marker-alt"></i> ${loc}</span></div></div>
      <div class="trend-description" style="margin-top:12px">${desc.substring(0, 200)}${desc.length > 200 ? '...' : ''}</div></div>`;
  }).join('');

  updateHartRecentEvents(events.slice(0, 5), histEvents.slice(0, 5));
  createHartCharts(events);
}

function updateHartRecentEvents(events: HartEvent[], histEvents: HartEvent[]): void {
  const container = document.getElementById('recentTrendsList');
  if (!container) return;
  if (events.length === 0) { container.innerHTML = '<div class="activity-placeholder"><i class="fas fa-inbox"></i><p>No recent events</p></div>'; return; }

  const curGroups = groupByCampaign(events);
  const histGroups = groupByCampaign(histEvents);

  const top = Object.entries(curGroups)
    .map(([type, evts]) => ({ type, evts, count: evts.length, change: calculateTrendChange(evts.length, histGroups[type]?.length || 0) }))
    .sort((a, b) => b.count - a.count).slice(0, 5);

  container.innerHTML = top.map(({ type, evts, count, change }) => {
    const sentCounts: Record<string, number> = {};
    evts.forEach(e => { const s = analyzeSentimentFromDescription(e.descBody); sentCounts[s] = (sentCounts[s] || 0) + 1; });
    const dominant = Object.entries(sentCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'neutral';
    const icon = dominant === 'positive' ? 'fa-smile' : dominant === 'negative' ? 'fa-frown' : 'fa-meh';
    const sample = (evts[0].descBody || '').substring(0, 100);
    return `<div class="activity-item" onclick='showHartCampaignPosts("${type}")'>
      <div class="activity-title" style="display:flex;align-items:center;gap:8px;justify-content:space-between"><span>${type}</span>${getTrendIndicatorHTML(change)}</div>
      <div class="activity-meta"><span><i class="fas fa-comments"></i> ${formatNumber(count)} events</span><span><i class="fas ${icon}"></i> ${dominant}</span>${sample ? `<span><i class="fas fa-info-circle"></i> ${sample}...</span>` : ''}</div></div>`;
  }).join('');
}

// ═══════════════════════════════════════════════════════════════════════════
// HART – Trends
// ═══════════════════════════════════════════════════════════════════════════

async function loadHartTrends(): Promise<void> {
  try {
    showLoading('Loading HART events (past 7 days)...');
    const { current, previous } = await fetchHartWeekly();
    hideLoading();
    STATE.hartEventsData = { events: current, totalCount: current.length };
    if (current.length === 0) { showTrendsPlaceholder('No HART events available'); return; }

    const curGroups = groupByCampaign(current);
    const histGroups = groupByCampaign(previous);

    const campaignType = (document.getElementById('campaignTypeFilter') as HTMLSelectElement)?.value;
    const groupsToShow = campaignType && campaignType !== 'all'
      ? Object.fromEntries(Object.entries(curGroups).filter(([type]) => type === campaignType))
      : curGroups;

    const container = document.getElementById('trendsGrid');
    if (!container) return;
    if (Object.keys(groupsToShow).length === 0) {
      showTrendsPlaceholder(campaignType && campaignType !== 'all'
        ? `No events found for campaign type "${campaignType}"`
        : 'No HART events available');
      return;
    }
    container.innerHTML = Object.entries(groupsToShow).map(([type, evts]) => {
      const change = calculateTrendChange(evts.length, histGroups[type]?.length || 0);
      const ind = getTrendIndicatorHTML(change);
      let hi = 0, med = 0, lo = 0;
      evts.forEach(e => { const r = determineRiskLevel(e.descBody || ''); if (r.level === 'HIGH') hi++; else if (r.level === 'MEDIUM') med++; else lo++; });
      const risk = hi > med && hi > lo ? 'HIGH' : med > lo ? 'MEDIUM' : 'LOW';
      const color = ML_CAMPAIGN_TYPES[type]?.color || '#6366f1';
      const kws = new Set<string>();
      evts.forEach(e => extractKeywordsFromText(e.descBody || '', 5).forEach(w => kws.add(w)));
      const kwArr = filterKeywords(Array.from(kws)).slice(0, 8);
      const sentCounts: Record<string, number> = { positive: 0, negative: 0, neutral: 0, mixed: 0 };
      evts.forEach(e => { sentCounts[analyzeSentimentFromDescription(e.descBody)]++; });
      const sent = Object.entries(sentCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'neutral';
      return `<div class="trend-card" onclick='showHartCampaignPosts("${type}")'>
        <div class="trend-header"><div style="display:flex;align-items:center;gap:8px;justify-content:space-between;flex-wrap:wrap;width:100%"><div><div class="trend-title" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">${type} ${ind}</div><div class="trend-type-badges"><span class="campaign-type-badge" style="background:${color}">${type}</span></div></div></div></div>
        <div class="trend-metrics"><div class="metric-item"><div class="metric-value">${evts.length}</div><div class="metric-label">Events</div></div><div class="metric-item"><div class="metric-value">${risk}</div><div class="metric-label">Risk</div></div><div class="metric-item"><div class="metric-value">${((evts.length / current.length) * 100).toFixed(0)}%</div><div class="metric-label">Share</div></div></div>
        <div class="campaign-distribution" style="margin:12px 0"><strong>Risk Breakdown:</strong>${hi ? `<span class="campaign-dist-item" style="color:#f56565">High: ${hi}</span>` : ''}${med ? `<span class="campaign-dist-item" style="color:#ed8936">Medium: ${med}</span>` : ''}${lo ? `<span class="campaign-dist-item" style="color:#48bb78">Low: ${lo}</span>` : ''}</div>
        <div class="topic-keywords" style="margin:8px 0">${kwArr.map(k => `<span class="keyword-tag">${k}</span>`).join('')}</div>
        <div class="trend-description">Sentiment: ${sent.charAt(0).toUpperCase() + sent.slice(1)}</div></div>`;
    }).join('');
  } catch (err: unknown) {
    hideLoading();
    showToast('Error loading HART trends: ' + (err as Error).message, 'error');
    showTrendsPlaceholder('Error loading HART events');
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// HART – Analytics
// ═══════════════════════════════════════════════════════════════════════════

async function loadHartAnalytics(): Promise<void> {
  try {
    let startDate = (document.getElementById('analyticsStartDate') as HTMLInputElement).value;
    let endDate = (document.getElementById('analyticsEndDate') as HTMLInputElement).value;
    if (!startDate || !endDate) {
      endDate = new Date().toISOString().split('T')[0];
      startDate = new Date(Date.now() - 7 * 86_400_000).toISOString().split('T')[0];
      (document.getElementById('analyticsStartDate') as HTMLInputElement).value = startDate;
      (document.getElementById('analyticsEndDate') as HTMLInputElement).value = endDate;
    }
    const start = new Date(startDate + 'T00:00:00Z').getTime();
    const end = new Date(endDate + 'T23:59:59.999Z').getTime();
    if (start > end) { showToast('Start date must be before end date', 'error'); return; }

    const PAGE_SIZE = 200;
    const MAX_PAGES = 20;
    const filtered: HartEvent[] = [];
    let page = 0;

    showLoading('Loading HART events...');
    while (page < MAX_PAGES) {
      const data = await api.getHartEvents(page, PAGE_SIZE);
      const batch = data.events || [];
      if (batch.length === 0) break;

      for (const ev of batch) {
        const ts = ev.createdAt || ev.reportedAt;
        if (!ts) continue;
        const t = new Date(ts).getTime();
        if (isNaN(t)) continue;
        if (t >= start && t <= end) filtered.push(ev);
      }
      page++;
      showLoading(`Loading HART events... (page ${page}, ${filtered.length} matches)`);
    }

    hideLoading();
    STATE.hartEventsData = { events: filtered, totalCount: filtered.length };
    if (filtered.length === 0) {
      showAnalyticsPlaceholder('No HART events in selected date range. Try a different range.');
      return;
    }
    requestAnimationFrame(() => setTimeout(() => createHartAnalyticsCharts(filtered), 50));
  } catch (err: unknown) {
    hideLoading();
    showAnalyticsPlaceholder('Error loading HART analytics');
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// HART – Event detail modals
// ═══════════════════════════════════════════════════════════════════════════

function showHartEventDetails(event: HartEvent, index: number): void {
  const desc = event.descBody || 'No description';
  const type = event.eventIncidentType || event.campaignType || 'General Alert';
  const risk = determineRiskLevel(desc);
  const dateMatch = desc.match(/(?:On|From)\s+(\d{1,2}\s+\w+\s+\d{4})/i);
  const date = dateMatch ? dateMatch[1] : 'Recent';

  openModal(`HART Security Event #${index + 1}`, `
    <div class="cluster-details"><div class="cluster-info-section"><h3>Event Details</h3>
      <div class="info-grid">
        <div class="info-item"><span class="info-label">Event Type:</span><span class="info-value">${type}</span></div>
        <div class="info-item"><span class="info-label">Risk Level:</span><span class="info-value" style="color:${risk.color}">${risk.level}</span></div>
        <div class="info-item"><span class="info-label">Date:</span><span class="info-value">${date}</span></div>
        <div class="info-item"><span class="info-label">Event #:</span><span class="info-value">${index + 1}</span></div>
      </div></div>
      <div class="posts-section"><h3>Full Description</h3>
        <div class="post-text" style="padding:16px;background:var(--bg-secondary);border-radius:8px;line-height:1.8;white-space:pre-wrap">${desc}</div>
        ${event.campaignType ? `<div style="margin-top:16px"><h4>Campaign Type:</h4><div class="topic-keywords"><span class="keyword-tag">${event.campaignType}</span></div></div>` : ''}
        ${event.eventIncidentType ? `<div style="margin-top:16px"><h4>Incident Type:</h4><div class="topic-keywords"><span class="keyword-tag">${event.eventIncidentType}</span></div></div>` : ''}
      </div></div>`);
}

async function showHartCampaignPosts(campaignType: string): Promise<void> {
  try {
    showLoading('Loading events...');
    const data = await api.getHartEvents(0, 100);
    hideLoading();
    const all = data.events || [];
    if (all.length === 0) { showToast('No HART events available', 'warning'); return; }

    const filtered = all.filter(e => {
      const t = e.mlCampaignType && e.mlCampaignType !== 'Unknown' ? e.mlCampaignType : classifyHartEventByCampaignType(e);
      return t === campaignType;
    });
    if (filtered.length === 0) { showToast(`No events found for ${campaignType}`, 'warning'); return; }

    const hi = filtered.filter(e => determineRiskLevel(e.descBody || '').level === 'HIGH').length;
    const med = filtered.filter(e => determineRiskLevel(e.descBody || '').level === 'MEDIUM').length;
    const lo = filtered.length - hi - med;
    const dominant = hi > med && hi > lo ? 'HIGH' : med > lo ? 'MEDIUM' : 'LOW';

    const kws = new Set<string>();
    filtered.forEach(e => extractKeywordsFromText(e.descBody || '', 5).forEach(w => kws.add(w)));
    const kwArr = filterKeywords(Array.from(kws)).slice(0, 10);

    const content = `<div class="cluster-details"><div class="cluster-info-section"><h3>Campaign Information</h3>
      <div class="info-grid">
        <div class="info-item"><span class="info-label">Total Events:</span><span class="info-value">${filtered.length}</span></div>
        <div class="info-item"><span class="info-label">Dominant Risk:</span><span class="info-value">${dominant}</span></div>
        <div class="info-item"><span class="info-label">Campaign Type:</span><span class="info-value">${campaignType}</span></div>
      </div>
      <div class="cluster-keywords-section"><h4>Keywords:</h4><div class="topic-keywords">${kwArr.map(k => `<span class="keyword-tag">${k}</span>`).join('')}</div></div>
      <div class="campaign-type-dist" style="margin-top:16px"><h4>Risk Distribution:</h4><div style="display:flex;gap:12px;margin-top:8px">
        <span style="background:#f56565;color:white;padding:6px 12px;border-radius:8px">High: ${hi}</span>
        <span style="background:#ed8936;color:white;padding:6px 12px;border-radius:8px">Medium: ${med}</span>
        <span style="background:#48bb78;color:white;padding:6px 12px;border-radius:8px">Low: ${lo}</span></div></div></div>
      <div class="posts-section"><h3>Events (${filtered.length})</h3><div class="posts-list">
        ${filtered.map((e, i) => {
          const r = determineRiskLevel(e.descBody || '');
          const d = extractDateFromDescription(e.descBody);
          const l = extractLocationFromDescription(e.descBody);
          return `<div class="post-item"><div class="post-header"><div class="post-meta"><span class="post-source"><i class="fas fa-shield-alt"></i> HART Event</span><span class="post-date">${d}</span></div><span class="risk-badge ${r.level.toLowerCase()}">${r.level}</span></div><div class="post-text">${e.descBody}</div>${l !== 'Unknown' ? `<div class="post-entities"><strong>Location:</strong><span class="entity-tag">${l}</span></div>` : ''}</div>`;
        }).join('')}</div></div></div>`;

    openModal(`${campaignType} Campaign`, content);
  } catch (err: unknown) {
    hideLoading();
    showToast(`Error loading events: ${(err as Error).message}`, 'error');
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// LifeRaft – Dashboard
// ═══════════════════════════════════════════════════════════════════════════

async function loadDashboardData(): Promise<void> {
  if (CONFIG.currentAPI === 'hart') { await loadHartDashboardData(); return; }
  const baseDate = new Date().toISOString().split('T')[0];
  try {
    showLoading(`Loading LifeRaft dashboard (${LIFERAFT_LOOKBACK_DAYS}-day window)...`);
    const [dashData, trendsData] = await Promise.all([
      api.getLiferaftDashboard(),
      api.getTrendsByDate(baseDate).catch(() => ({ trends: [], total_trends: 0 })),
    ]);
    hideLoading();

    const rangeTopics = (dashData.current.topics || []) as TopicCluster[];
    rangeTopics.forEach(t => { t.analysis_date = baseDate; });
    const histTopics = (dashData.previous.topics || []) as TopicCluster[];

    const merged = mergeClusters(rangeTopics);
    const mergedHist = mergeClusters(histTopics);
    const histMap: Record<string, number> = {};
    mergedHist.forEach(t => { const k = t.dominant_campaign_type || 'Unknown'; histMap[k] = (histMap[k] || 0) + (t.size || 0); });
    merged.forEach(t => { const k = t.dominant_campaign_type || 'Unknown'; t.trendChange = calculateTrendChange(t.size || 0, histMap[k] || 0); });

    const trendsPayload = trendsData as { trends?: Trend[]; total_trends?: number };
    updateDashboardMetrics(trendsPayload, { topics: merged, total_topics: merged.length, allTopics: rangeTopics });
    const dailyVolumes = (dashData.current as { daily_volumes?: DailyVolume[] }).daily_volumes;
    if (dailyVolumes?.length) {
      createSpikesChartFromDailyVolumes(dailyVolumes);
    } else {
      createSpikesChartFromTopics(rangeTopics);
    }
    createSentimentChartFromTopics(rangeTopics);
    createCampaignTypeChartFromTopics(merged);
    updateTopTopics(merged);
    displayTopics(merged);
  } catch (err: unknown) {
    console.error('Error loading dashboard data:', err);
    showToast('Error loading dashboard data', 'error');
    showDashboardErrorPlaceholder('Failed to load dashboard data');
  }
}

function updateDashboardMetrics(trendsData: { trends?: Trend[]; total_trends?: number } | null, topicsData: { topics: TopicCluster[]; total_topics: number; allTopics: TopicCluster[] }): void {
  const topics = topicsData.topics;
  const all = topicsData.allTopics || topics;
  document.getElementById('totalTrends')!.textContent = String(topics.length);
  document.getElementById('totalTopics')!.textContent = String(topicsData.total_topics);
  document.getElementById('totalVolume')!.textContent = formatNumber(all.reduce((s, t) => s + (t.size || 0), 0));

  if (all.length > 0) {
    let ts = 0, tw = 0;
    all.forEach(t => { if (t.dominant_sentiment && t.size) { ts += getSentimentValue(t.dominant_sentiment) * t.size; tw += t.size; } });
    document.getElementById('avgSentiment')!.textContent = (((tw > 0 ? ts / tw : 0) + 1) / 2 * 10).toFixed(1);
  } else {
    document.getElementById('avgSentiment')!.textContent = '5.0';
  }
  ['trendsChange','topicsChange','volumeChange','sentimentChange'].forEach(id => { const el = document.getElementById(id); if (el) el.style.display = 'none'; });
}

function updateTopTopics(topics: TopicCluster[]): void {
  const container = document.getElementById('recentTrendsList');
  if (!container) return;
  if (!topics?.length) {
    const msg = CONFIG.currentAPI === 'liferaft'
      ? 'No topics yet. Data comes from the API (MongoDB topic clusters).'
      : 'No topics available';
    container.innerHTML = `<div class="activity-placeholder"><i class="fas fa-inbox"></i><p>${msg}</p></div>`;
    return;
  }

  const top5 = [...topics].sort((a, b) => b.size - a.size).slice(0, 5);
  container.innerHTML = top5.map(t => {
    const kw = t.keywords.slice(0, 3).join(', ');
    const s = t.dominant_sentiment || 'neutral';
    const si = s === 'positive' ? 'fa-smile' : s === 'negative' ? 'fa-frown' : 'fa-meh';
    const camp = t.dominant_campaign_type || 'Unknown';
    const ind = getTrendIndicatorHTML(t.trendChange);
    let spike = '';
    if (t.trendChange) {
      const c = t.trendChange.isRising ? '#48bb78' : '#f56565';
      const ic = t.trendChange.isRising ? 'fa-arrow-up' : 'fa-arrow-down';
      const pct = t.trendChange.isRising ? `+${t.trendChange.percent}%` : `-${t.trendChange.percent}%`;
      spike = `<div style="margin-top:8px;padding:8px;background:${c}15;border-left:3px solid ${c};border-radius:4px"><div style="display:flex;align-items:center;gap:8px;font-size:0.85em"><i class="fas ${ic}" style="color:${c}"></i><span style="color:${c};font-weight:600">${pct}</span><span style="color:#666;font-size:0.75em">vs. previous ${LIFERAFT_COMPARE_DAYS} days</span></div></div>`;
    }
    const ids = t.cluster_ids ? t.cluster_ids.join(',') : t.id;
    return `<div class="activity-item" onclick="showMergedTopicPosts('${ids}')">
      <div class="activity-title" style="display:flex;align-items:center;gap:8px;justify-content:space-between"><span>${camp}</span>${ind}</div>
      <div class="activity-meta"><span><i class="fas fa-comments"></i> ${formatNumber(t.size)} posts</span><span><i class="fas ${si}"></i> ${s}</span>${kw ? `<span><i class="fas fa-tags"></i> ${kw}</span>` : ''}</div>${spike}</div>`;
  }).join('');
}

// ═══════════════════════════════════════════════════════════════════════════
// LifeRaft – Trends
// ═══════════════════════════════════════════════════════════════════════════

async function loadTrends(): Promise<void> {
  if (CONFIG.currentAPI === 'hart') { await loadHartTrends(); return; }
  const trendType = (document.getElementById('trendType') as HTMLSelectElement).value;
  const campaignType = (document.getElementById('campaignTypeFilter') as HTMLSelectElement).value;

  try {
    showLoading(`Loading trends (past ${LIFERAFT_LOOKBACK_DAYS} days)...`);
    const dashData = await api.getLiferaftDashboard();
    hideLoading();

    const topics = (dashData.current.topics || []) as TopicCluster[];
    const mergedPrev = mergeClusters((dashData.previous.topics || []) as TopicCluster[]);
    const histMap: Record<string, number> = {};
    mergedPrev.forEach(t => { const k = t.dominant_campaign_type || 'Unknown'; histMap[k] = (histMap[k] || 0) + (t.size || 0); });

    let trends: Trend[] = topics.map(t => {
      const k = t.dominant_campaign_type || 'Unknown';
      return {
        id: t.id,
        title: k,
        trend_type: 'Emerging',
        campaign_type: t.dominant_campaign_type,
        current_volume: t.size || 0,
        spike_ratio: 1,
        confidence_score: (t.coherence_score || 0.5),
        novelty_score: 0.8,
        keywords: t.keywords || [],
        description: `Topic with ${t.size || 0} posts`,
        campaign_type_distribution: t.campaign_type_distribution,
        dominant_sentiment: t.dominant_sentiment,
        trendChange: calculateTrendChange(t.size || 0, histMap[k] || 0),
      };
    });

    if (trendType !== 'all') trends = trends.filter(t => t.trend_type.toLowerCase() === trendType.toLowerCase());
    if (campaignType !== 'all') trends = trends.filter(t => t.campaign_type === campaignType);

    if (!trends.length) {
      showTrendsPlaceholder(`No trends detected for the past ${LIFERAFT_LOOKBACK_DAYS} days. Keep collecting data!`);
      return;
    }
    displayTrends(trends);
  } catch {
    hideLoading();
    showTrendsPlaceholder('No trends detected yet. Keep collecting data!');
  }
}

function displayTrends(trends: Trend[]): void {
  const container = document.getElementById('trendsGrid');
  if (!container) return;
  if (!trends?.length) { showTrendsPlaceholder('No trends found for the selected date and filters'); return; }

  container.innerHTML = trends.map(t => {
    const ind = getTrendIndicatorHTML(t.trendChange);
    const kw = (t.keywords || []).slice(0, 5);
    const sent = t.dominant_sentiment ? t.dominant_sentiment.charAt(0).toUpperCase() + t.dominant_sentiment.slice(1) : '';
    return `<div class="trend-card" onclick="showTrendDetails('${t.id}')">
      <div class="trend-header"><div style="display:flex;align-items:center;gap:8px;justify-content:space-between;flex-wrap:wrap;width:100%"><div>
        <div class="trend-title" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">${t.title} ${ind}</div>
        <div class="trend-type-badges"><span class="trend-type-badge ${t.trend_type.toLowerCase()}">${t.trend_type}</span>${t.campaign_type ? `<span class="campaign-type-badge">${t.campaign_type}</span>` : ''}</div></div></div></div>
      <div class="trend-metrics"><div class="metric-item"><div class="metric-value">${formatNumber(t.current_volume)}</div><div class="metric-label">Volume</div></div><div class="metric-item"><div class="metric-value">${t.spike_ratio.toFixed(1)}x</div><div class="metric-label">Spike</div></div><div class="metric-item"><div class="metric-value">${Math.round(t.confidence_score * 100)}%</div><div class="metric-label">Confidence</div></div><div class="metric-item"><div class="metric-value">${Math.round(t.novelty_score * 100)}%</div><div class="metric-label">Novelty</div></div></div>
      ${t.campaign_type_distribution && Object.keys(t.campaign_type_distribution).length ? `<div class="campaign-distribution"><strong>Campaign Types:</strong>${Object.entries(t.campaign_type_distribution).map(([k, v]) => `<span class="campaign-dist-item">${k}: ${v}</span>`).join('')}</div>` : ''}
      <div class="trend-keywords">${kw.map(k => `<span class="keyword-tag">${k}</span>`).join('')}</div>
      <div class="trend-description">Sentiment: ${sent || '—'}</div></div>`;
  }).join('');
}

async function detectTrends(): Promise<void> {
  const date = STATE.currentDate;
  try { await api.detectTrends(date); showToast('Trend detection started!', 'success'); setTimeout(() => loadTrends(), 5000); }
  catch { showToast('Error starting trend detection', 'error'); }
}

// ═══════════════════════════════════════════════════════════════════════════
// LifeRaft – Topics
// ═══════════════════════════════════════════════════════════════════════════

async function loadTopics(): Promise<void> {
  await loadTrends();
}

function displayTopics(topics: TopicCluster[]): void {
  const isDash = STATE.currentSection === 'dashboard';
  const container = document.getElementById(isDash ? 'dashboardTopicsGrid' : 'topicsGrid');
  if (!container) return;
  if (!topics?.length) {
    const msg = CONFIG.currentAPI === 'liferaft'
      ? `No topic clusters for the past ${LIFERAFT_LOOKBACK_DAYS} days. Ensure MongoDB has events and topic detection has run, or try another date range.`
      : `No topic clusters found for the past ${LIFERAFT_LOOKBACK_DAYS} days`;
    container.innerHTML = `<div class="topics-placeholder"><i class="fas fa-tags"></i><p>${msg}</p></div>`;
    return;
  }

  let header = '';
  if (isDash && CONFIG.currentAPI === 'liferaft') {
    header = `<div style="grid-column:1/-1;background:linear-gradient(135deg,#11998e 0%,#38ef7d 100%);color:white;padding:16px;border-radius:12px;margin-bottom:16px;display:flex;align-items:center;gap:12px;box-shadow:0 4px 12px rgba(17,153,142,0.3)"><i class="fas fa-life-ring" style="font-size:1.5rem"></i><div><div style="font-weight:600;font-size:1.125rem">LifeRaft Trend Detection</div><div style="font-size:0.875rem;opacity:0.9">Social media trends and topic clusters</div></div><div style="margin-left:auto;background:rgba(255,255,255,0.2);padding:8px 16px;border-radius:20px;font-weight:600">${topics.length} Topics</div></div>`;
  }

  container.innerHTML = header + topics.map(t => {
    let dateDisp = '';
    if (t.date_range) dateDisp = `<span class="topic-date">${t.date_range}</span>`;
    else if (t.analysis_date) dateDisp = `<span class="topic-date">${formatDateRelative(t.analysis_date)}</span>`;
    const ind = getTrendIndicatorHTML(t.trendChange);
    const ids = JSON.stringify(t.cluster_ids || [t.id]);
    return `<div class="topic-card" onclick='showMergedTopicPosts(${ids})'>
      <div class="topic-header"><div class="topic-title" style="display:flex;align-items:center;gap:8px;justify-content:space-between;flex-wrap:wrap"><div>${t.dominant_campaign_type || 'Topic Cluster'} ${dateDisp}</div>${ind}</div></div>
      <div class="topic-metrics"><div class="metric-item"><div class="metric-value">${t.size || 0}</div><div class="metric-label">Posts</div></div><div class="metric-item"><div class="metric-value">${Math.round((t.coherence_score || 0) * 100)}%</div><div class="metric-label">Coherence</div></div></div>
      <div class="topic-keywords">${filterKeywords(t.keywords || []).slice(0, 8).map(k => `<span class="keyword-tag">${k}</span>`).join('')}</div>
      <div class="trend-description">Sentiment: ${t.dominant_sentiment || 'Mixed'}</div></div>`;
  }).join('');
}

function mergeClusters(clusters: TopicCluster[]): TopicCluster[] {
  const byType: Record<string, TopicCluster[]> = {};
  clusters.forEach(c => { const t = c.dominant_campaign_type || 'Unknown'; (byType[t] ??= []).push(c); });

  const merged: TopicCluster[] = [];
  for (const [type, arr] of Object.entries(byType)) {
    if (arr.length === 1) { merged.push(arr[0]); continue; }
    const postSet = new Set<string>();
    const kwCounts: Record<string, number> = {};
    let cohSum = 0;
    const ids: string[] = [];
    arr.forEach(c => {
      ids.push(c.id);
      c.posts?.forEach(p => postSet.add(p));
      c.keywords?.forEach(k => { kwCounts[k] = (kwCounts[k] || 0) + 1; });
      if (c.coherence_score) cohSum += c.coherence_score;
    });
    const dates = arr.map(c => c.analysis_date || c.date).filter(Boolean);
    merged.push({
      id: arr[0].id, date: arr[0].date, dominant_campaign_type: type,
      posts: Array.from(postSet), keywords: Object.entries(kwCounts).sort((a, b) => b[1] - a[1]).slice(0, 15).map(([k]) => k),
      size: postSet.size, coherence_score: cohSum / arr.length,
      dominant_sentiment: arr[0].dominant_sentiment, cluster_ids: ids,
      date_range: dates.length > 0 ? `Past ${dates.length} days` : undefined,
    });
  }
  return merged.sort((a, b) => b.size - a.size);
}

async function detectTopics(): Promise<void> {
  const date = new Date().toISOString().split('T')[0];
  const method = (document.getElementById('clusterMethod') as HTMLSelectElement).value;
  try { showLoading('Detecting topics...'); await api.detectTopics(date, method); hideLoading(); showToast('Topic detection started!', 'success'); setTimeout(() => loadTrends(), 5000); }
  catch { hideLoading(); showToast('Error starting topic detection', 'error'); }
}

// ═══════════════════════════════════════════════════════════════════════════
// LifeRaft – Topic post modals
// ═══════════════════════════════════════════════════════════════════════════

async function showTopicPosts(clusterId: string): Promise<void> {
  try {
    showLoading('Loading cluster posts...');
    const data = await api.getClusterPosts(clusterId);
    hideLoading();
    const ci = data.cluster_info;
    const content = `<div class="cluster-details"><div class="cluster-info-section"><h3>Cluster Information</h3>
      <div class="info-grid">
        <div class="info-item"><span class="info-label">Total Posts:</span><span class="info-value">${data.total_posts}</span></div>
        <div class="info-item"><span class="info-label">Coherence:</span><span class="info-value">${Math.round((ci.coherence_score || 0) * 100)}%</span></div>
        <div class="info-item"><span class="info-label">Sentiment:</span><span class="info-value">${ci.dominant_sentiment || 'Mixed'}</span></div>
        ${ci.dominant_campaign_type ? `<div class="info-item"><span class="info-label">Campaign:</span><span class="info-value">${ci.dominant_campaign_type}</span></div>` : ''}
      </div>
      <div class="cluster-keywords-section"><h4>Keywords:</h4><div class="topic-keywords">${ci.keywords.map(k => `<span class="keyword-tag">${k}</span>`).join('')}</div></div>
      ${ci.campaign_type_distribution && Object.keys(ci.campaign_type_distribution).length ? `<div class="campaign-dist-section"><h4>Campaign Type Distribution:</h4><div class="campaign-dist-chart">${Object.entries(ci.campaign_type_distribution).map(([t, c]) => `<div class="campaign-dist-bar"><span class="dist-label">${t}</span><div class="dist-bar-container"><div class="dist-bar" style="width:${(c / data.total_posts) * 100}%"></div><span class="dist-count">${c}</span></div></div>`).join('')}</div></div>` : ''}
    </div>
    <div class="posts-section"><h3>Posts (${data.total_posts}${data.has_more ? `, showing ${data.posts.length}` : ''})</h3><div class="posts-list">${data.posts.map(p => _postHTML(p)).join('')}</div></div></div>`;
    openModal('Topic Cluster Posts', content);
  } catch (err: unknown) {
    hideLoading();
    showToast(`Error loading cluster posts: ${(err as Error).message}`, 'error');
  }
}

async function showMergedTopicPosts(clusterIds: string | string[]): Promise<void> {
  const ids = typeof clusterIds === 'string' ? clusterIds.split(',') : clusterIds;
  try {
    showLoading('Loading cluster posts...');
    const results = await Promise.all(ids.map(id => api.getClusterPosts(id).catch(() => ({ posts: [], total_posts: 0, cluster_info: { keywords: [] } }))));
    hideLoading();
    const postIds = new Set<string>();
    const allPosts: Post[] = [];
    const ci = results[0]?.cluster_info || { keywords: [] };
    results.forEach(r => r.posts?.forEach(p => { if (!postIds.has(p.id)) { postIds.add(p.id); allPosts.push(p); } }));
    allPosts.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    const totalPosts = results.reduce((sum, r) => sum + (r.total_posts || 0), 0);
    const content = `<div class="cluster-details"><div class="cluster-info-section"><h3>Cluster Information (Past ${LIFERAFT_LOOKBACK_DAYS} Days)</h3>
      <div class="info-grid">
        <div class="info-item"><span class="info-label">Total Posts:</span><span class="info-value">${totalPosts}</span></div>
        <div class="info-item"><span class="info-label">Coherence:</span><span class="info-value">${Math.round(((ci as {coherence_score?: number}).coherence_score || 0) * 100)}%</span></div>
        <div class="info-item"><span class="info-label">Sentiment:</span><span class="info-value">${(ci as {dominant_sentiment?: string}).dominant_sentiment || 'Mixed'}</span></div>
        ${(ci as {dominant_campaign_type?: string}).dominant_campaign_type ? `<div class="info-item"><span class="info-label">Campaign:</span><span class="info-value">${(ci as {dominant_campaign_type?: string}).dominant_campaign_type}</span></div>` : ''}
      </div>
      <div class="cluster-keywords-section"><h4>Keywords:</h4><div class="topic-keywords">${ci.keywords.map(k => `<span class="keyword-tag">${k}</span>`).join('')}</div></div></div>
      <div class="posts-section"><h3>Posts (${totalPosts}${allPosts.length < totalPosts ? `, showing ${allPosts.length}` : ''})</h3><div class="posts-list">${allPosts.map(p => _postHTML(p)).join('')}</div></div></div>`;

    const modal = document.getElementById('trendModal')!;
    const mc = modal.querySelector('.modal-content')!;
    mc.innerHTML = `<button class="modal-close" onclick="closeTrendModal()">&times;</button>${content}`;
    modal.classList.add('active');
    modal.style.cssText = 'display:flex !important;position:fixed !important;top:0 !important;left:0 !important;width:100% !important;height:100% !important;z-index:9999 !important;background:rgba(0,0,0,0.7) !important;align-items:center !important;justify-content:center !important;';
    document.body.style.overflow = 'hidden';
  } catch (err: unknown) {
    hideLoading();
    showToast(`Error loading cluster posts: ${(err as Error).message}`, 'error');
  }
}

function _postHTML(p: { source?: string; timestamp?: string; author?: string; sentiment?: string; cleaned_text?: string; text?: string; campaign_type?: string; keywords?: string[]; entities?: Array<{text: string; label: string}>; url?: string }): string {
  return `<div class="post-item"><div class="post-header"><div class="post-meta">
    <span class="post-source"><i class="fas fa-${getSourceIcon(p.source)}"></i> ${p.source}</span>
    <span class="post-date">${p.timestamp ? new Date(p.timestamp).toLocaleDateString() : ''}</span>
    ${p.author ? `<span class="post-author"><i class="fas fa-user"></i> ${p.author}</span>` : ''}</div>
    ${p.sentiment ? `<span class="sentiment-badge sentiment-${p.sentiment.toLowerCase()}">${p.sentiment}</span>` : ''}</div>
    <div class="post-text">${p.cleaned_text || p.text}</div>
    ${p.campaign_type ? `<div class="post-campaign"><span class="campaign-type-badge">${p.campaign_type}</span></div>` : ''}
    ${p.keywords?.length ? `<div class="post-keywords">${p.keywords.slice(0, 5).map(k => `<span class="keyword-tag-small">${k}</span>`).join('')}</div>` : ''}
    ${p.entities?.length ? `<div class="post-entities"><strong>Entities:</strong>${p.entities.slice(0, 5).map(e => `<span class="entity-tag">${e.text} (${e.label})</span>`).join('')}</div>` : ''}
    ${p.url ? `<div class="post-footer"><a href="${p.url}" target="_blank" class="post-link"><i class="fas fa-external-link-alt"></i> View Original</a></div>` : ''}</div>`;
}

function showTrendDetails(trendId: string): void {
  openModal('Trend Details', `<p>Detailed information for trend ID: ${trendId}</p><p>This feature will be implemented with more detailed trend analysis.</p>`);
}

// ═══════════════════════════════════════════════════════════════════════════
// LifeRaft – Analytics
// ═══════════════════════════════════════════════════════════════════════════

async function loadAnalytics(): Promise<void> {
  if (CONFIG.currentAPI === 'hart') { await loadHartAnalytics(); return; }
  let startDate = (document.getElementById('analyticsStartDate') as HTMLInputElement).value;
  let endDate = (document.getElementById('analyticsEndDate') as HTMLInputElement).value;
  if (!startDate || !endDate) {
    endDate = new Date().toISOString().split('T')[0];
    startDate = new Date(Date.now() - 7 * 86_400_000).toISOString().split('T')[0];
    (document.getElementById('analyticsStartDate') as HTMLInputElement).value = startDate;
    (document.getElementById('analyticsEndDate') as HTMLInputElement).value = endDate;
  }
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (start > end) { showToast('Start date must be before end date', 'error'); return; }
  const today = new Date().toISOString().split('T')[0];
  const days = Math.min(90, Math.ceil((end.getTime() - start.getTime()) / 86_400_000) + 1);
  const endOffsetDays = Math.max(
    0,
    Math.floor((new Date(today + 'T00:00:00').getTime() - end.getTime()) / 86_400_000),
  );
  try {
    showLoading(`Loading analytics (${days}-day window)...`);
    const rangeData = await api.getTopicsByRange(days, endOffsetDays) as { topics?: TopicCluster[] };
    hideLoading();
    const all = (rangeData.topics || []) as TopicCluster[];
    all.forEach(t => { t.analysis_date = endDate; });
    if (all.length > 0) { createTopicAnalyticsCharts(mergeClusters(all)); showToast(`Analytics loaded: ${all.length} topic clusters`, 'success'); }
    else showAnalyticsPlaceholder('No topic clusters in this range. Check MongoDB data or selected dates.');
  } catch (err: unknown) {
    hideLoading();
    showAnalyticsPlaceholder('Error loading analytics: ' + (err as Error).message);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Settings & Theme
// ═══════════════════════════════════════════════════════════════════════════

function loadSettings(): void {
  const saved = localStorage.getItem('hartSettings');
  if (saved) {
    try { Object.assign(CONFIG, JSON.parse(saved)); } catch { /* ignore corrupt data */ }
  }
}

function applyTheme(theme: string): void {
  document.documentElement.setAttribute('data-theme', theme);
  CONFIG.theme = theme;
}

// ═══════════════════════════════════════════════════════════════════════════
// Auto-refresh
// ═══════════════════════════════════════════════════════════════════════════

function startAutoRefresh(): void {
  if (STATE.refreshTimer) clearInterval(STATE.refreshTimer);
  STATE.autoRefreshEnabled = true;
  STATE.refreshTimer = setInterval(() => refreshData(), CONFIG.refreshInterval);
}

function stopAutoRefresh(): void {
  if (STATE.refreshTimer) { clearInterval(STATE.refreshTimer); STATE.refreshTimer = null; }
  STATE.autoRefreshEnabled = false;
}

async function refreshData(): Promise<void> {
  if (!STATE.isConnected) { await checkConnection(); return; }
  await loadCurrentSectionData();
}

function refreshChart(_chartId: string): void {
  loadCurrentSectionData();
}

// ═══════════════════════════════════════════════════════════════════════════
// Global event listeners
// ═══════════════════════════════════════════════════════════════════════════

document.addEventListener('click', e => {
  if ((e.target as HTMLElement).classList.contains('modal')) (e.target as HTMLElement).classList.remove('active');
});
document.addEventListener('keydown', e => {
  if (e.key === 'Escape') document.querySelectorAll('.modal.active').forEach(m => m.classList.remove('active'));
});

// ═══════════════════════════════════════════════════════════════════════════
// Window exports (required for inline onclick handlers in HTML)
// ═══════════════════════════════════════════════════════════════════════════

const w = window as unknown as Record<string, unknown>;
w.switchAPI = switchAPI;
w.showSection = showSection;
w.loadDashboardData = loadDashboardData;
w.refreshData = refreshData;
w.loadTrends = loadTrends;
w.detectTrends = detectTrends;
w.loadAnalytics = loadAnalytics;
w.refreshChart = refreshChart;
w.closeTrendModal = closeTrendModal;
w.showHartEventDetails = showHartEventDetails;
w.showHartCampaignPosts = showHartCampaignPosts;
w.showMergedTopicPosts = showMergedTopicPosts;
w.showTopicPosts = showTopicPosts;
w.showTrendDetails = showTrendDetails;
w.showToast = showToast;

w.HART = { CONFIG, STATE, api, showSection, loadDashboardData, loadTrends, loadAnalytics, detectTrends, showTopicPosts, refreshData, showToast };

